import {CAPTURE_FILE_PREFIX} from '../config'

/**
 * Turning the engine's screenshot payload into a shareable file.
 *
 * Pure by design - no DOM beyond the `Blob`/`File` constructors, and no
 * reference to XR8. `src/engine/xr8-facade.ts` deliberately stops at the base64
 * string the engine hands back, so everything below can be reasoned about (and
 * exercised) without a camera, a canvas or a running pipeline.
 */

const JPEG_MIME = 'image/jpeg'

const pad = (value: number, width = 2): string => String(value).padStart(width, '0')

/**
 * `webar-20260727-143052.jpg`, in the phone's local time.
 *
 * Local, not UTC: this name is the only label the user ever sees on the file,
 * and a photo taken at 23:40 filed under tomorrow's date is a small mystery
 * nobody needs. Colons are avoided outright - some share targets and file
 * systems reject them, and the failure shows up as a silent no-op.
 */
export const buildPhotoFileName = (now: Date): string =>
  [
    CAPTURE_FILE_PREFIX,
    `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}`,
    `${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}.jpg`,
  ].join('-')

/**
 * Decodes the engine's base64 JPEG into a `File`.
 *
 * `atob` is the right tool here despite the byte-at-a-time loop: the payload is
 * a few hundred KB, which costs single-digit milliseconds, and the alternative
 * (`fetch` on a data: URI) is asynchronous - an extra await between the capture
 * and the preview for no benefit.
 *
 * The MIME type is set on BOTH the Blob and the File on purpose. `canShare` is
 * specified to test the file against a per-target allow-list, and a `File` with
 * an empty type is rejected outright on some Android builds - which reads at the
 * call site as "this device cannot share photos" when in fact it can.
 */
export const jpegFromBase64 = (base64: string, fileName: string): File => {
  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return new File([new Blob([bytes], {type: JPEG_MIME})], fileName, {type: JPEG_MIME})
}
